import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, X, Tag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import api, { formatApiError } from "../lib/api";

export const CartDrawer = () => {
  const {
    items, drawerOpen, setDrawerOpen, setQty, removeItem,
    subtotal, discount, discountAmount, total, applyDiscount, clearDiscount,
  } = useCart();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setCheckingOut(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.id, size: i.size, qty: i.qty, color: i.color || null })),
        email: email || null,
        discount_code: discount?.code || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 bg-[#0C0A09]/75 backdrop-blur-sm z-50"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-[#171412] border-l border-[#2C2623] z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#2C2623]">
              <h2 className="font-display font-extrabold uppercase tracking-tight text-lg">Your Cart</h2>
              <button onClick={() => setDrawerOpen(false)} className="text-[#B8AEA5] hover:text-[#E05A36] transition-colors" data-testid="cart-close-button">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.length === 0 && (
                <p className="text-sm text-[#7A7067] py-12 text-center" data-testid="cart-empty-message">
                  Your cart is empty. The rebellion needs you.
                </p>
              )}
              {items.map((item) => (
                <div key={`${item.id}-${item.size}-${item.color || "na"}`} className="flex gap-4 bg-[#1E1A17] border border-[#2C2623] p-3" data-testid={`cart-item-${item.id}-${item.size}-${item.color || "na"}`}>
                  <img src={item.image} alt={item.name} className="w-16 h-20 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{item.name}</p>
                    <p className="text-xs font-mono text-[#7A7067] uppercase mt-0.5">
                      {item.size !== "ONE SIZE" ? `Size ${item.size}` : "One size"}{item.color ? ` · ${item.color}` : ""}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#2C2623]">
                        <button onClick={() => setQty(item.id, item.size, item.color, item.qty - 1)} className="px-2 py-1 hover:text-[#E05A36]" data-testid={`cart-qty-minus-${item.id}-${item.size}-${item.color || "na"}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-2 text-xs font-mono" data-testid={`cart-qty-${item.id}-${item.size}-${item.color || "na"}`}>{item.qty}</span>
                        <button onClick={() => setQty(item.id, item.size, item.color, item.qty + 1)} className="px-2 py-1 hover:text-[#E05A36]" data-testid={`cart-qty-plus-${item.id}-${item.size}-${item.color || "na"}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-sm font-mono text-[#E29D47]">${(item.price * item.qty).toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.id, item.size, item.color)} className="self-start text-[#7A7067] hover:text-[#E05A36] transition-colors" data-testid={`cart-remove-${item.id}-${item.size}-${item.color || "na"}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#2C2623] px-6 py-5 space-y-4">
                {discount ? (
                  <div className="flex items-center justify-between bg-[#E05A36]/10 border border-[#E05A36]/40 px-3 py-2" data-testid="cart-discount-applied">
                    <span className="text-xs font-mono uppercase tracking-widest text-[#E05A36] flex items-center gap-2">
                      <Tag className="w-3 h-3" /> {discount.code} applied
                    </span>
                    <button onClick={clearDiscount} className="text-[#7A7067] hover:text-[#E05A36]" data-testid="cart-discount-remove-button">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="DISCOUNT CODE"
                      className="flex-1 bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
                      data-testid="cart-discount-input"
                    />
                    <button
                      onClick={() => code && applyDiscount(code)}
                      className="border border-[#2C2623] hover:border-[#E05A36] hover:text-[#E05A36] px-4 text-xs font-bold uppercase tracking-wider transition-colors"
                      data-testid="cart-discount-apply-button"
                    >
                      Apply
                    </button>
                  </div>
                )}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for order updates (optional)"
                  className="w-full bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-sm transition-colors"
                  data-testid="cart-email-input"
                />
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-[#B8AEA5]">
                    <span>Subtotal</span>
                    <span className="font-mono" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-[#E05A36]">
                      <span>Discount</span>
                      <span className="font-mono" data-testid="cart-discount-amount">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base pt-1 border-t border-[#2C2623]">
                    <span>Total</span>
                    <span className="font-mono text-[#E29D47]" data-testid="cart-total">${total.toFixed(2)}</span>
                  </div>
                </div>
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  className="w-full bg-[#E05A36] text-[#0C0A09] font-bold uppercase tracking-wider py-4 hover:bg-[#F06B47] hover:shadow-[0_0_25px_rgba(224,90,54,0.4)] active:scale-[0.98] transition-all disabled:opacity-50"
                  data-testid="cart-checkout-button"
                >
                  {checkingOut ? "Redirecting to checkout..." : "Checkout — Guest OK"}
                </button>
                <p className="text-[10px] text-center font-mono text-[#7A7067] uppercase tracking-widest">
                  Secure payment via Stripe • Tax calculated at checkout
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
